import { parse } from 'yaml';
import { api, desenvolver, ErrorApi } from '../api';

/**
 * Traer el contrato de la API (KAN-184), desde `GET /api/v1/contrato`.
 *
 * # POR QUÉ SE PIDE CON EL CLIENTE HTTP Y NO SE ENLAZA
 *
 * Mismo problema —y misma salida— que el PDF (`comprobantes/pdf.ts`): el
 * endpoint exige `Authorization: Bearer`, el navegador no manda ese header al
 * seguir un enlace ni al cargar un `iframe`, y la sesión de este front vive solo
 * en memoria (KAN-142). Un `<a href="/api/v1/contrato">` daría `401` siempre. Y
 * el token no va a la query string.
 *
 * # POR QUÉ NO ESTÁ EN EL BUNDLE
 *
 * Sería más simple importar `docs/openapi.yaml` al build y mostrarlo sin pedir
 * nada. No se hace por dos motivos: **un archivo que forma parte del bundle lo
 * sirve nginx sin credencial** —justo lo que se decidió no hacer, el contrato
 * describe la superficie completa, `/admin` incluido—, y además mostraría el
 * contrato **con el que se compiló el front**, que puede no ser el del backend
 * desplegado. Pedirlo al backend garantiza que lo que se lee es lo que corre.
 */

/** Lo mínimo que esta pantalla necesita saber de un documento OpenAPI. */
export interface DocumentoOpenApi {
  readonly info?: { readonly title?: string; readonly version?: string };
  readonly [clave: string]: unknown;
}

export interface Contrato {
  /** El YAML tal cual llegó: es lo que se ofrece para descargar. */
  readonly texto: string;
  /** El mismo contenido, ya interpretado, para el visor. */
  readonly documento: DocumentoOpenApi;
  /** `info.version`, o `null` si el documento no la trae. */
  readonly version: string | null;
}

export async function obtenerContrato(): Promise<Contrato> {
  // `parseAs: 'text'`: la respuesta es `application/yaml`, y sin esto el cliente
  // intentaría leerla como JSON y rompería con un contrato perfectamente válido.
  const texto = await desenvolver(api.GET('/api/v1/contrato', { parseAs: 'text' }));
  return interpretar(texto);
}

/**
 * Del texto al documento. Separado de {@link obtenerContrato} para poder
 * probarlo sin red.
 *
 * Un YAML que no se puede leer, o que no es un documento, se trata como una
 * respuesta ilegible del servicio (con su código local) y no como un error del
 * front: para quien mira la pantalla el efecto es el mismo, y ese es el código
 * que ya sabe traducir a un mensaje.
 */
export function interpretar(texto: string): Contrato {
  let documento: unknown;
  try {
    documento = parse(texto);
  } catch {
    throw ErrorApi.deRespuesta(200, undefined);
  }
  if (documento === null || typeof documento !== 'object' || Array.isArray(documento)) {
    throw ErrorApi.deRespuesta(200, undefined);
  }
  const doc = documento as DocumentoOpenApi;
  return { texto, documento: doc, version: doc.info?.version ?? null };
}
